import * as React from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Loading from "../../components/loading";
import {gql} from "@apollo/client";
import {mutate} from "../../lib/apollo";
import {toast} from "react-toastify";

interface LeadBulkUnsubscribeResult {
    affectedLeadCount: number;
    phoneCount: number;
    emailCount: number;
    ipCount: number;
    md5Count: number;
    phones: string[];
    emails: string[];
    ips: string[];
    md5s: string[];
    invalidLines: string[];
    notificationSent: boolean;
}

export default function UnsubscribePage() {
    const [lines, setLines] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<LeadBulkUnsubscribeResult | null>(null);

    React.useEffect(() => {
        document.title = "Unsubscribe | DN";
    }, []);

    const handleSubmit = async () => {
        if (!lines.trim()) {
            toast.error("Enter at least one phone, email, IP, or MD5");
            return;
        }

        setLoading(true);
        setResult(null);

        const q = gql`
            mutation LeadBulkUnsubscribe($lines: String!) {
                leadBulkUnsubscribe(lines: $lines) {
                    affectedLeadCount
                    phoneCount
                    emailCount
                    ipCount
                    md5Count
                    phones
                    emails
                    ips
                    md5s
                    invalidLines
                    notificationSent
                }
            }
        `;

        const response = await mutate(q, {lines});
        setLoading(false);

        if (!response?.leadBulkUnsubscribe) {
            // Keep submitted values so the admin can correct/retry
            return;
        }

        const data = response.leadBulkUnsubscribe as LeadBulkUnsubscribeResult;
        setResult(data);
        toast.success(`${data.affectedLeadCount} leads unsubscribed`);
    };

    return (
        <>
            <h2>Unsubscribe</h2>
            <p>Enter phone, email, IP, or MD5 — one per line (or a combination).</p>

            <Box sx={{display: "flex", alignItems: "flex-start", gap: 2, maxWidth: 720}}>
                <TextField
                    id="unsubscribe-lines"
                    label="Leads"
                    multiline
                    minRows={8}
                    fullWidth
                    placeholder={"4805551212\nuser@example.com\n8.8.8.8"}
                    value={lines}
                    onChange={(e) => setLines(e.target.value)}
                    disabled={loading}
                />
                <Button
                    onClick={handleSubmit}
                    autoFocus
                    variant="contained"
                    disabled={loading}
                    sx={{mt: 1, whiteSpace: "nowrap"}}
                >
                    Unsubscribe
                </Button>
            </Box>

            {loading ? <Loading/> : null}

            {result ? (
                <Box sx={{mt: 3, maxWidth: 720}}>
                    <Alert severity="success" sx={{mb: 2}}>
                        {result.affectedLeadCount} leads unsubscribed
                        {result.notificationSent
                            ? " — results emailed to Paul"
                            : " — result email failed to send"}
                    </Alert>

                    <Box component="ul" sx={{pl: 3, mb: 2}}>
                        <li>Phones: {result.phoneCount}</li>
                        <li>Emails: {result.emailCount}</li>
                        <li>IPs: {result.ipCount}</li>
                        <li>MD5s: {result.md5Count}</li>
                    </Box>

                    {result.invalidLines.length > 0 ? (
                        <Alert severity="warning">
                            <div>Invalid / unrecognized lines ({result.invalidLines.length}):</div>
                            <Box component="pre" sx={{m: 0, mt: 1, whiteSpace: "pre-wrap"}}>
                                {result.invalidLines.join("\n")}
                            </Box>
                        </Alert>
                    ) : null}
                </Box>
            ) : null}
        </>
    );
}
